<template>
  <div class="tours-activities-ads-item">
    <div class="tours-activities-ads-item__info">
      <EHeading
        as="h2"
        class="tours-activities-ads-item__title"
        data-test-id="tours-activities-ads-item__title"
        :aria-label="title"
      >
        <ELink
          :href="url"
          :title="title"
          color="result"
          class="tours-activities-ads-item__link"
          data-test-id="tours-activities-ads-item__link"
          @click="onClick"
        >
          {{ title }}
        </ELink>
      </EHeading>
      <ERating
        class="tours-activities-ads-item__rating"
        data-test-id="tours-activities-ads-item__rating"
        :value="ratings"
        :reviews="numberOfRatings"
      />
      <span
        class="tours-activities-ads-item__duration"
        data-test-id="tours-activities-ads-item__duration"
        :aria-label="durationProviderLabel"
      >
        {{ durationProviderDisplay }}
      </span>
      <span
        v-safe-html="formattedPriceDisplay"
        class="tours-activities-ads-item__price"
        data-test-id="tours-activities-ads-item__price"
        :aria-label="startingPriceLabel"
      />
      <span
        class="tours-activities-ads-item__domain"
        data-test-id="tours-activities-ads-item__domain"
        :aria-label="domainLabel"
      >
        {{ domain }}
      </span>
    </div>
    <div
      class="tours-activities-ads-item__image"
      data-test-id="tours-activities-ads-item__image"
    >
      <ELink
        :href="url"
        :title="title"
        data-test-id="tours-activities-ads-item__image-link"
        @click="onClick"
      >
        <EThumbnail
          :src="imageUrl"
          :alt="title"
          class="tours-activities-ads-item__thumbnail"
          data-test-id="tours-activities-ads-item__thumbnail"
        />
      </ELink>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { formatPrice } from '~~/common/js/universal/intl';
import { getDurationFromTimeString } from '~~/common/js/universal/datetime';
import { trackImpression, trackClick, TYPE_TOURS_ACTIVITIES_AD } from '~~/search/common/js/client/analytics/results';
import { baseProps } from '~~/search/common/vue/components/results/base';
import EHeading from '~~/common/vue/components/heading';
import ELink from '~~/common/vue/components/link';
import ERating from '~~/common/vue/components/rating';
import EThumbnail from '~~/common/vue/components/thumbnail';

const TIME_DAYS = 'days';
const TIME_HOURS = 'hours';
const TIME_MINUTES = 'minutes';

export default {
  name: 'ETourActivityAdsItem',
  components: {
    EHeading,
    ELink,
    ERating,
    EThumbnail,
  },
  props: {
    ...baseProps,
    pingSuffix: {
      type: String,
      default: '',
    },
    trackingId: {
      type: String,
      default: '',
    },
    imageUrl: {
      type: String,
      default: '',
    },
    startingPrice: {
      type: Object,
      default: null,
    },
    durations: {
      type: String,
      default: '',
    },
    ratings: {
      type: Number,
      default: 0,
    },
    numberOfRatings: {
      type: Number,
      default: 0,
    },
    category: {
      type: String,
      default: '',
    },
    adProvider: {
      type: String,
      default: '',
    },
    domain: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    formattedDuration() {
      const { days, hours, minutes } = getDurationFromTimeString(this.durations);

      // if we didn't get a parsable result, we return the original string
      if (days === 0 && hours === 0 && minutes === 0) {
        return this.durations;
      }

      let formattedString = '';

      const getTranslation = (value, type) => {
        const quantitySpecifier = value > 1 ? 'plural' : 'singular';
        return `${value}${this.$t(`common.generic.time.${type}.${quantitySpecifier}.short`)}`;
      };

      if (minutes > 0) {
        formattedString = getTranslation(minutes, TIME_MINUTES);
      }

      if (hours > 0) {
        formattedString = getTranslation(hours, TIME_HOURS);

        if (minutes > 0) {
          formattedString += ` ${getTranslation(minutes, TIME_MINUTES)}`;
        }
      }

      if (days > 0) {
        formattedString = getTranslation(days, TIME_DAYS);

        if (hours > 0) {
          formattedString += ` ${getTranslation(hours, TIME_HOURS)}`;
        }

        if (minutes > 0) {
          formattedString += ` ${getTranslation(minutes, TIME_MINUTES)}`;
        }
      }

      return formattedString;
    },
    formattedPrice() {
      let formatted = '';

      if (this.startingPrice && this.startingPrice.currency) {
        formatted = formatPrice(
          this.locale,
          this.startingPrice?.currency,
          this.startingPrice?.value,
        );
      }

      return formatted;
    },
    formattedPriceDisplay() {
      return this.$t('common.search.results.toursactivitiesads.startingPrice', { value: this.formattedPrice });
    },
    durationProviderDisplay() {
      if (this.formattedDuration === '') {
        return this.adProvider;
      }
      return `${this.formattedDuration} · ${this.adProvider}`;
    },
    durationProviderLabel() {
      return this.$t('common.search.results.toursactivitiesads.durationProviderLabel',
        {
          duration: this.formattedDuration,
          provider: this.adProvider,
        });
    },
    startingPriceLabel() {
      return this.$t('common.search.results.toursactivitiesads.startingPriceLabel', { value: this.formattedPrice });
    },
    domainLabel() {
      return this.$t('common.search.results.toursactivitiesads.domainLabel', { domain: this.domain });
    },
    trackingOptions() {
      return {
        position: 'sidebar',
        rank: this.rank,
        title: this.title,
        type: TYPE_TOURS_ACTIVITIES_AD,
        url: this.source,
      };
    },
  },
  mounted() {
    trackImpression(this.trackingOptions);
  },
  methods: {
    onClick() {
      trackClick(this.trackingOptions);
    },
  },
};
</script>

<style lang="scss" scoped>
.tours-activities-ads-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: $space-1s 0;
}

.tours-activities-ads-item__info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;

  span {
    @include line-clamp(1);
  }
}

.tours-activities-ads-item__title {
  font-size: $font-l;
  font-weight: $font-weight-m;
  line-height: $line-height-m;

  @include line-clamp(2);
}

.tours-activities-ads-item__duration {
  color: var(--color-text-secondary);
}

.tours-activities-ads-item__image {
  border-radius: $border-radius-m;
  padding-left: $space-m;
}

.tours-activities-ads-item__thumbnail {
  border-radius: $border-radius-m;
  overflow: hidden;
  height: 80px;
  width: 80px;

  &:hover {
    @include elevation(2);
  }
}
</style>
